<template>
    <v-form lazy-validation v-model="valid" ref="form">
        <v-row>
            <v-col cols="12" md="9">
                <v-card
                    class="d-flex flex-column white rounded pa-9 d-flex items-center justify-start"
                    :loading="loading"
                >
                    <v-row align="center" justify="space-between">
                        <v-col cols="12" md="10" class="d-flex align-items-start">
                            <v-card-title class="secondary--text font-weight-bold">
                                {{ $t('teachers.general_information') }}
                            </v-card-title>
                        </v-col>
                        <v-col
                            cols="12"
                            md="2"
                            class="d-flex align-items-start justify-content-end"
                        >
                            <v-btn
                                fab
                                small
                                color="deep-purple lighten-1"
                                class="ml-auto"
                                dark
                                :disabled="loading || !isDirty"
                                :loading="saving"
                                :title="$t('teachers.save_changes')"
                                @click="onSave()"
                            >
                                <v-icon> mdi-content-save </v-icon>
                            </v-btn>
                            <v-btn
                                fab
                                small
                                color="indigo lighten-1"
                                class="ml-2"
                                dark
                                :disabled="loading || !userToSave.dni"
                                :title="$t('students.impersonate')"
                                @click="impersonateUser"
                            >
                                <v-icon> mdi-account-switch </v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-expand-transition>
                        <div
                            v-if="isDirty"
                            class="unsaved-bar d-flex align-center justify-space-between mt-2"
                        >
                            <span class="caption font-weight-medium amber--text text--darken-4">
                                <v-icon x-small color="amber darken-3">
                                    mdi-alert-circle-outline
                                </v-icon>
                                {{ $t('teachers.unsaved_changes') }}
                            </span>
                            <v-btn x-small text @click="discardChanges">
                                {{ $t('teachers.discard_changes') }}
                            </v-btn>
                        </div>
                    </v-expand-transition>
                    <v-col cols="12">
                        <v-row align="center" justify="center" class="mb-1">
                            <v-col>
                                <span>ID: </span>
                                <span class="deep-purple--text font-weight-bold">{{
                                    teacherId
                                }}</span>
                            </v-col>
                            <v-col>
                                <span>{{ $t('students.creation_date') }}: </span>
                                <span class="deep-purple--text font-weight-bold">{{
                                    created | moment('MM/D/YYYY')
                                }}</span>
                            </v-col>
                            <v-col>
                                <span>{{ $t('students.last_access') }}: </span>
                                <span class="deep-purple--text font-weight-bold d-block">
                                    {{ lastAccessDate || $t('students.without_last_access') }}
                                </span>
                            </v-col>
                        </v-row>
                        <v-divider />
                    </v-col>

                    <!-- Editable data -->
                    <!-- Personal information -->
                    <v-row align="start" justify="center" class="mt-3">
                        <v-col cols="12" md="5">
                            <v-row align="start" justify="start" class="d-flex flex-column">
                                <span class="body black--text my-1">{{
                                    $t('students.country')
                                }}</span>
                                <v-autocomplete
                                    :items="countries"
                                    item-value="id"
                                    item-text="name"
                                    filled
                                    flat
                                    dense
                                    :placeholder="$t('students.country')"
                                    v-model="userToSave.country"
                                    :rules="requiredRule('country')"
                                >
                                    <template v-slot:selection="{ item }">
                                        <img
                                            :src="`${flagsPath}${item.iso_code}.png`"
                                            alt="country flag"
                                            width="35vh"
                                            height="25vh"
                                            class="mr-2"
                                        />
                                        <span class="">{{ item.name }}</span>
                                    </template>
                                    <template v-slot:item="{ item }">
                                        <template>
                                            <img
                                                :src="`${flagsPath}${item.iso_code}.png`"
                                                alt="country flag"
                                                width="35vh"
                                                height="25vh"
                                                class="mr-2"
                                            />
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    v-html="capitalize(item.name)"
                                                ></v-list-item-title>
                                            </v-list-item-content>
                                        </template>
                                    </template>
                                </v-autocomplete>
                            </v-row>
                            <v-row align="start" justify="start" class="d-flex flex-column mt-3">
                                <span class="body black--text my-1">{{ $t('students.dni') }}</span>
                                <v-text-field
                                    filled
                                    flat
                                    dense
                                    :placeholder="$t('students.dni')"
                                    :rules="dniRule"
                                    v-model="userToSave.dni"
                                />
                            </v-row>
                            <v-row align="start" justify="start" class="d-flex flex-column">
                                <span class="body black--text my-1">{{ $t('teachers.name') }}</span>
                                <v-text-field
                                    filled
                                    flat
                                    dense
                                    :placeholder="$t('teachers.name')"
                                    :rules="requiredRule('firstname')"
                                    v-model="userToSave.firstname"
                                />
                            </v-row>
                            <v-row align="start" justify="start" class="d-flex flex-column">
                                <span class="body black--text my-1">{{
                                    $t('students.student_parent_lastname')
                                }}</span>
                                <v-text-field
                                    dense
                                    :placeholder="$t('students.student_parent_lastname')"
                                    filled
                                    flat
                                    v-model="userToSave.lastname"
                                    :rules="requiredRule('lastname')"
                                />
                            </v-row>
                            <v-row align="start" justify="start" class="d-flex flex-column">
                                <span class="body black--text my-1">{{
                                    $t('students.student_mother_lastname')
                                }}</span>
                                <v-text-field
                                    dense
                                    :placeholder="$t('students.student_mother_lastname')"
                                    filled
                                    flat
                                    v-model="userToSave.lastname2nd"
                                />
                            </v-row>

                            <v-row align="start" justify="start" class="d-flex flex-column">
                                <span class="body black--text my-1">{{
                                    $t('students.email')
                                }}</span>
                                <v-text-field
                                    dense
                                    :placeholder="$t('students.email')"
                                    filled
                                    flat
                                    v-model="userToSave.email"
                                />
                            </v-row>
                            <v-row align="start" justify="start" class="d-flex flex-column">
                                <span class="body black--text my-1">{{
                                    $t('students.birthdate')
                                }}</span>
                                <!-- DATE PICKER -->
                                <v-menu
                                    v-model="menu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="userToSave.birthDate"
                                            append-icon="mdi-calendar"
                                            :placeholder="$t('students.birthdate')"
                                            readonly
                                            filled
                                            dense
                                            flat
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="userToSave.birthDate"
                                        :active-picker.sync="activePicker"
                                        :max="
                                            new Date(
                                                Date.now() - new Date().getTimezoneOffset() * 60000
                                            )
                                                .toISOString()
                                                .substr(0, 10)
                                        "
                                        min="1990-01-01"
                                        :locale="$i18n.locale"
                                        @input="menu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </v-row>
                        </v-col>
                        <v-divider vertical class="mx-1" />

                        <!-- College information -->
                        <v-col cols="12" md="6">
                            <v-row align="center" justify="center" class="d-flex flex-column">
                                <span class="black--text">{{ $t('students.state') }}</span>
                                <v-radio-group v-model="userToSave.active" row>
                                    <v-radio
                                        :label="$t('students.active')"
                                        :value="true"
                                        color="deep-purple lighten-1"
                                    />
                                    <v-radio
                                        :label="$t('students.inactive')"
                                        :value="false"
                                        color="deep-purple lighten-1"
                                    />
                                </v-radio-group>
                            </v-row>
                            <v-divider />

                            <v-col cols="12" class="pa-3">
                                <v-row align="start" justify="center" class="d-flex flex-column">
                                    <span class="body black--text my-1">{{
                                        $t('students.school')
                                    }}</span>
                                    <v-autocomplete
                                        class="w-100"
                                        filled
                                        flat
                                        dense
                                        v-model="userToSave.school"
                                        :items="schoolList"
                                        item-value="value"
                                        item-text="text"
                                        :placeholder="$t('students.select_school')"
                                        :rules="requiredRule('school')"
                                    />
                                </v-row>

                                <v-row align="start" justify="center" class="d-flex flex-column">
                                    <span class="body black--text my-1">
                                        {{ $t('teachers.courses_assigned') }}
                                        <span class="grey--text">
                                            ({{ activeCourseCount }})
                                        </span>
                                    </span>

                                    <div
                                        v-if="activeCourseChips.length"
                                        class="d-flex flex-wrap mb-2"
                                        style="gap: 8px;"
                                    >
                                        <v-chip
                                            v-for="chip in activeCourseChips"
                                            :key="chip.id"
                                            label
                                            :color="chip.color"
                                            :title="chip.title"
                                            :close="chip.state !== 'removed'"
                                            close-icon="mdi-close"
                                            @click:close="toggleRemoveCourse(chip.id)"
                                        >
                                            <v-icon
                                                v-if="chip.otherSchoolName"
                                                left
                                                small
                                            >
                                                mdi-bank-outline
                                            </v-icon>
                                            <span
                                                :class="chip.state === 'removed'
                                                    ? 'course-removed-text'
                                                    : 'black--text'"
                                            >
                                                {{ chip.label }}
                                            </span>
                                            <span
                                                v-if="chip.otherSchoolName"
                                                class="ml-1 chip-other-text"
                                            >
                                                · {{ chip.otherSchoolName }}
                                            </span>
                                            <span
                                                v-if="chip.state === 'added'"
                                                class="ml-1 caption green--text text--darken-2"
                                            >
                                                · {{ $t('teachers.course_new') }}
                                            </span>
                                            <v-btn
                                                v-if="chip.state === 'removed'"
                                                x-small
                                                text
                                                class="ml-1 px-1 course-undo-btn"
                                                @click="toggleRemoveCourse(chip.id)"
                                            >
                                                <v-icon x-small left>mdi-undo-variant</v-icon>
                                                {{ $t('teachers.undo') }}
                                            </v-btn>
                                        </v-chip>
                                    </div>

                                    <div
                                        v-else
                                        class="mb-2 w-100 course-empty"
                                    >
                                        {{ $t('teachers.no_courses_assigned') }}
                                    </div>

                                    <!-- KB-971: la app de docentes no muestra cursos inactivos.
                                         Se listan aparte y sin quitar: sus ids siguen en
                                         userToSave.courses para que el save no los borre. -->
                                    <template v-if="inactiveCourseChips.length">
                                        <span class="caption grey--text text--darken-1 mb-1">
                                            {{ $t('teachers.inactive_courses') }}
                                            ({{ inactiveCourseChips.length }})
                                        </span>
                                        <div class="d-flex flex-wrap mb-2" style="gap: 8px;">
                                            <v-chip
                                                v-for="chip in inactiveCourseChips"
                                                :key="chip.id"
                                                label
                                                outlined
                                                color="grey"
                                                :title="chip.title"
                                            >
                                                <v-icon left small>mdi-archive-outline</v-icon>
                                                <span class="grey--text text--darken-1">
                                                    {{ chip.label }}
                                                </span>
                                                <span
                                                    v-if="chip.otherSchoolName"
                                                    class="ml-1 chip-other-text"
                                                >
                                                    · {{ chip.otherSchoolName }}
                                                </span>
                                                <span class="ml-1 caption grey--text">
                                                    · {{ $t('teachers.course_inactive') }}
                                                </span>
                                            </v-chip>
                                        </div>
                                    </template>

                                    <span class="body black--text mt-2 mb-1">{{
                                        $t('teachers.add_course')
                                    }}</span>
                                    <v-autocomplete
                                        filled
                                        flat
                                        dense
                                        clearable
                                        :items="availableCourses"
                                        v-model="courseSearch"
                                        item-value="id"
                                        item-text="displayName"
                                        prepend-inner-icon="mdi-magnify"
                                        :placeholder="$t('students.select_courses')"
                                        :no-data-text="$t('teachers.no_courses_available')"
                                        @change="addCourse"
                                    />
                                </v-row>

                                <v-dialog v-model="saveSummaryDialog" max-width="460" persistent>
                                    <v-card>
                                        <v-card-title class="text-h6 font-weight-bold">
                                            {{ $t('teachers.save_summary_title') }}
                                        </v-card-title>
                                        <v-card-text>
                                            <p class="mb-3">
                                                {{ $t('teachers.save_summary_intro') }}
                                            </p>
                                            <div
                                                v-for="chip in coursesDiff.added"
                                                :key="`add-${chip.id}`"
                                                class="mb-1 d-flex align-center"
                                            >
                                                <v-icon small color="green darken-1" class="mr-1">
                                                    mdi-plus
                                                </v-icon>
                                                <span class="green--text text--darken-2 mr-1">
                                                    {{ $t('teachers.summary_add') }}
                                                </span>
                                                <span class="black--text">{{ chip.label }}</span>
                                            </div>
                                            <div
                                                v-for="chip in coursesDiff.removed"
                                                :key="`rm-${chip.id}`"
                                                class="mb-2"
                                            >
                                                <div class="d-flex align-center">
                                                    <v-icon small color="red darken-1" class="mr-1">
                                                        mdi-minus
                                                    </v-icon>
                                                    <span class="red--text text--darken-2 mr-1">
                                                        {{ $t('teachers.summary_remove') }}
                                                    </span>
                                                    <span class="black--text">
                                                        {{ chip.label }}
                                                    </span>
                                                </div>
                                                <div
                                                    v-if="chip.otherSchoolName"
                                                    class="caption red--text text--darken-2 ml-6"
                                                >
                                                    {{ chip.otherSchoolName }}
                                                    {{ $t('teachers.remove_course_other_school') }}
                                                </div>
                                            </div>
                                        </v-card-text>
                                        <v-card-actions>
                                            <v-spacer />
                                            <v-btn text @click="saveSummaryDialog = false">
                                                {{ $t('common.cancel') }}
                                            </v-btn>
                                            <v-btn
                                                color="primary"
                                                text
                                                :loading="saving"
                                                @click="confirmSave"
                                            >
                                                {{ $t('teachers.save_changes') }}
                                            </v-btn>
                                        </v-card-actions>
                                    </v-card>
                                </v-dialog>
                            </v-col>
                        </v-col>
                    </v-row>
                </v-card>
            </v-col>

            <v-col cols="12" md="3" class="h-100">
                <v-row align="center" justify="center" class="h-100">
                    <v-col cols="12" class="pb-0">
                        <v-alert rounded outlined light color="deep-purple" class="pa-2 mb-0">
                            <h4 class="text-left deep-purple--text lighten-2 font-weight-semibold">
                                Acciones:
                            </h4>
                            <v-btn
                                rounded
                                small
                                color="deep-purple lighten-1"
                                dark
                                @click.prevent="openChangePassword()"
                            >
                                {{ $t('students.create_new_information') }}
                            </v-btn>
                        </v-alert>
                    </v-col>
                </v-row>
            </v-col>

            <!-- MODALS FOR THE PAGE -->
            <general-modal-container
                @onClose="closeChangePassword()"
                v-if="isChangePasswordModalOpen"
            >
                <student-change-password-modal
                    :student-name="teacherInfo.name"
                    :student-username="teacherInfo.username"
                    :student-id="teacherInfo.id"
                    :country-id="teacherInfo.country_id"
                    v-model="newPassword"
                    :disabled-buttons="modalLoading"
                    @onSave="onSavePassword()"
                    @onCancel="closeChangePassword()"
                />
            </general-modal-container>

            <!-- Impersonate App Selector Modal -->
            <general-modal-container v-if="isImpersonateModalOpen" @onClose="closeImpersonateModal">
                <impersonate-app-selector-modal
                    :user-name="impersonateUserLabel"
                    @onSelect="handleImpersonateAppSelect"
                    @onCancel="closeImpersonateModal"
                />
            </general-modal-container>

            <v-dialog v-model="leaveDialog" max-width="420" persistent>
                <v-card>
                    <v-card-title class="text-h6 font-weight-bold">
                        {{ $t('teachers.unsaved_changes') }}
                    </v-card-title>
                    <v-card-text>{{ $t('teachers.leave_confirm') }}</v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn text @click="cancelLeave">{{ $t('common.cancel') }}</v-btn>
                        <v-btn color="error" text @click="confirmLeave">
                            {{ $t('teachers.discard_changes') }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
    </v-form>
</template>

<script>
// Modals
import GeneralModalContainer from '@/components/core/modals/GeneralModalContainer.vue';
import StudentChangePasswordModal from '@/components/students/StudentChangePasswordModal.vue';
import ImpersonateAppSelectorModal from '@/components/teachers/modals/ImpersonateAppSelectorModal.vue';

import { mapState } from 'vuex';
import LoadingMixin from '../../mixins/loading';
import SnackbarMixin from '../../mixins/snackbar';
import userService from '../../services/user.service';
import courseService from '../../services/course.service';
import sectionService from '../../services/section.service';
import schoolService from '../../services/school.service';
import impersonationService from '../../services/impersonation.service';
import { FLAGS_URL } from '../../constants/data';
import { isCourseInactive } from '../../utils/teacherCourses';
import {
  buildImpersonationLabel,
  buildImpersonationPayload,
  getImpersonationErrorMessage,
} from '../../utils/impersonation';

export default {
  name: 'TeacherEdit',
  props: { teacherId: { type: [Number, String], default: 0 } },
  components: {
    GeneralModalContainer,
    StudentChangePasswordModal,
    ImpersonateAppSelectorModal,
  },
  data: () => ({
    isActive: false,
    status: true,
    isChangePasswordModalOpen: false,
    isImpersonateModalOpen: false,
    activePicker: null,
    flagsPath: `${FLAGS_URL}countries_flags/`,
    date: null,
    menu: false,
    dniValidationOutputErrors: [],
    courses: [],
    assignedCourses: [],
    courseSearch: null,
    originalCourseIds: [],
    originalSnapshot: null,
    saveSummaryDialog: false,
    saving: false,
    leaveDialog: false,
    pendingLeaveNext: null,
    created: null,
    lastAccessDate: null,
    userToSave: {
      id: null,
      firstname: null,
      lastname: null,
      lastname2nd: null,
      dni: null,
      birthDate: null,
      country: null,
      active: true,
      school: null,
      courses: [],
    },
    grade_id: null,
    school_id: null,
    valid: true,
    newPassword: '',
    modalLoading: false,
    schoolList: [],
  }),
  computed: {
    ...mapState({
      countries: ({ common: { countries } }) => countries,
    }),
    dniRule() {
      return [
        (v) => !!v || this.$t('students.dni_required'),
        (v) => (v && v.length >= 4) || this.$t('students.dni_length_valid'),
      ];
    },
    sectionRule() {
      return [(v) => v > -1 || v !== null || this.$t('students.section_required')];
    },
    teacherInfo() {
      return this.userToSave
        ? {
          name: `${this.userToSave.firstname} ${this.userToSave.lastname}`,
          username: this.userToSave.dni,
          id: this.userToSave.id,
          country_id: this.userToSave.country,
        }
        : null;
    },
    impersonateUserLabel() {
      return this.userToSave ? buildImpersonationLabel(this.userToSave) : '';
    },
    courseById() {
      const byId = new Map();
      [...this.courses, ...this.assignedCourses].forEach((course) => {
        byId.set(String(course.id), course);
      });
      return byId;
    },
    assignedCourseChips() {
      const current = new Set(this.userToSave.courses.map((id) => String(id)));
      const original = new Set(this.originalCourseIds.map((id) => String(id)));
      const ids = [...new Set([...original, ...current])];
      return ids.map((id) => {
        const course = this.courseById.get(id);
        const label = course ? this.courseDisplayName(course) : `#${id}`;
        const isOtherSchool = !!course && course.school_id != null
          && this.userToSave.school != null
          && String(course.school_id) !== String(this.userToSave.school);
        const otherSchoolName = isOtherSchool ? this.schoolName(course.school_id) : null;
        const inactive = isCourseInactive(course);
        let state = 'current';
        if (current.has(id) && !original.has(id)) state = 'added';
        else if (!current.has(id) && original.has(id)) state = 'removed';
        let color = otherSchoolName ? 'deep-purple lighten-5' : 'grey lighten-3';
        if (state === 'added') color = 'green lighten-5';
        if (state === 'removed') color = 'grey lighten-4';
        return {
          id,
          label,
          otherSchoolName,
          inactive,
          state,
          color,
          title: otherSchoolName ? `${label} · ${otherSchoolName}` : label,
        };
      });
    },
    activeCourseChips() {
      return this.assignedCourseChips.filter((chip) => !chip.inactive);
    },
    inactiveCourseChips() {
      return this.assignedCourseChips.filter((chip) => chip.inactive);
    },
    activeCourseCount() {
      return this.activeCourseChips.filter((chip) => chip.state !== 'removed').length;
    },
    coursesDiff() {
      const chips = this.assignedCourseChips;
      return {
        added: chips.filter((chip) => chip.state === 'added'),
        removed: chips.filter((chip) => chip.state === 'removed'),
      };
    },
    coursesChangeCount() {
      return this.coursesDiff.added.length + this.coursesDiff.removed.length;
    },
    hasRemovedOtherSchool() {
      return this.coursesDiff.removed.some((chip) => chip.otherSchoolName);
    },
    formSnapshot() {
      const u = this.userToSave;
      return JSON.stringify({
        firstname: u.firstname,
        lastname: u.lastname,
        lastname2nd: u.lastname2nd,
        dni: u.dni,
        email: u.email,
        country: u.country,
        active: u.active,
        school: u.school,
        birthDate: u.birthDate,
        courses: [...u.courses].map((id) => String(id)).sort(),
      });
    },
    isDirty() {
      return this.originalSnapshot !== null && this.originalSnapshot !== this.formSnapshot;
    },
    availableCourses() {
      const assigned = new Set(this.userToSave.courses.map((courseId) => String(courseId)));
      return this.courses
        .filter((course) => !assigned.has(String(course.id)))
        .map((course) => ({
          id: String(course.id),
          displayName: this.courseDisplayName(course),
        }));
    },
  },
  async mounted() {
    this.loader();
    try {
      const { status, data } = await userService.findOne(this.teacherId);
      this.sections = [...(await this.findSections())];
      if (status !== 200) {
        this.snackbarError(this.$t('students.student_not_found'));
      } else {
        const user = { ...data };
        this.userToSave.birthDate = user.birthDate
          ? this.$moment(user.birthDate).format('YYYY-MM-DD')
          : '';
        this.userToSave.id = user.id;
        this.userToSave.dni = user.dni;
        this.userToSave.firstname = user.firstname;
        this.userToSave.lastname = user.lastname;
        this.userToSave.lastname2nd = user.lastname2nd;
        this.userToSave.email = user.email;
        this.userToSave.active = user.active;
        this.userToSave.country = user.country?.id;
        this.userToSave.school = user.school_id;
        this.assignedCourses = user.courseteachers || [];
        this.userToSave.courses = this.assignedCourses.map((course) => String(course.id));
        this.created = user.creationDate;
        this.lastAccessDate = user.lastAccessDate
          ? this.$moment(user.lastAccessDate).format('HH:mm MM/D/YYYY')
          : null;
        this.originalCourseIds = [...this.userToSave.courses];
        this.originalSnapshot = this.formSnapshot;
      }
    } catch (e) {
      this.snackbarError(this.$t('students.student_not_found'));
    } finally {
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
      this.loader();
    }
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      // Si ya había una navegación pendiente sin resolver, la cancelamos antes de pisar el next.
      if (this.pendingLeaveNext) this.pendingLeaveNext(false);
      this.pendingLeaveNext = next;
      this.leaveDialog = true;
    } else {
      next();
    }
  },
  methods: {
    async findSections() {
      const { status, data } = await sectionService.findAll();
      if (status !== 200) return [];
      return data;
    },
    async getSchools(country) {
      try {
        const { data, status } = await schoolService.getListByCountry(country);
        if (status === 200) this.schoolList = [...data];
        else this.schoolList = [];
      } catch (e) {
        this.snackbarError(e.message);
      }
    },
    async getCourses(schoolId) {
      try {
        const { data, status } = await courseService.findAllBySchool(schoolId);
        if (status === 200) this.courses = [...data];
        else this.courses = [];
      } catch (e) {
        this.snackbarError(e.message);
      }
    },
    async onSavePassword() {
      this.modalLoading = !this.modalLoading;
      if (this.userToSave && this.userToSave.dni) {
        const { dni, country } = this.userToSave;
        const { status } = await userService.changePassword({
          dni,
          country,
          password: this.newPassword,
        });
        if (status !== 200) this.snackbarError(this.$t('students.new_password_not_saved'));
        else this.snackbarSuccess(this.$t('students.new_password_was_set'));
      }

      this.modalLoading = !this.modalLoading;
      this.isChangePasswordModalOpen = false;
    },
    openChangePassword() {
      this.isChangePasswordModalOpen = true;
    },
    closeChangePassword() {
      this.isChangePasswordModalOpen = false;
    },
    // Entry point del guardado: valida y, si hay bajas de otra sede, pide confirmar.
    onSave() {
      if (!this.$refs.form.validate()) return;
      if (this.hasRemovedOtherSchool) {
        this.saveSummaryDialog = true;
        return;
      }
      this.doSave();
    },
    async confirmSave() {
      this.saveSummaryDialog = false;
      await this.doSave();
    },
    async doSave() {
      if (this.saving) return;
      this.saving = true;
      this.loader();
      try {
        const { status } = await userService.update({ ...this.userToSave }, 'TEACHER');
        if (status !== 200) {
          this.snackbarError(this.$t('error.user_not_saved'));
        } else {
          this.snackbarSuccess(this.$t('students.user_saved'));
          // Re-sincroniza el snapshot: el form vuelve a estado "limpio" sin recargar.
          this.originalCourseIds = [...this.userToSave.courses];
          this.originalSnapshot = this.formSnapshot;
        }
      } catch (error) {
        this.snackbarError(this.$t('error.user_not_saved'));
      } finally {
        this.saving = false;
        this.loader();
      }
    },
    async impersonateUser() {
      const payload = buildImpersonationPayload(this.userToSave);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }
      this.isImpersonateModalOpen = true;
    },
    closeImpersonateModal() {
      this.isImpersonateModalOpen = false;
    },
    async handleImpersonateAppSelect(app) {
      this.closeImpersonateModal();
      const useTeachersApp = app === 'teachers';
      await this.executeImpersonation(useTeachersApp);
    },
    async executeImpersonation(useTeachersApp) {
      const payload = buildImpersonationPayload(this.userToSave);
      if (!payload) {
        this.snackbarError(this.$t('students.action_error'));
        return;
      }

      this.loader();
      try {
        if (useTeachersApp) {
          const response = await impersonationService.impersonate(payload);
          const redirectURL = response?.content?.redirectURL;
          if (!redirectURL) {
            const errorMessage = response?.content ?? this.$t('students.action_error');
            throw new Error(String(errorMessage));
          }
          const label = buildImpersonationLabel(this.userToSave);
          this.snackbarSuccess(
            `${this.$t('students.impersonate_opening')} ${label}`.trim(),
          );
          window.open(redirectURL, '_blank');
          return;
        }

        const response = await impersonationService.impersonateForDataSystem(payload);

        if (!response?.redirectURL) {
          throw new Error(this.$t('students.action_error'));
        }

        const label = buildImpersonationLabel(this.userToSave);
        this.snackbarSuccess(
          `${this.$t('students.impersonate_opening')} ${label}`.trim(),
        );
        window.open(response.redirectURL, '_blank');
      } catch (error) {
        const errorMessage = getImpersonationErrorMessage(
          error,
          this.$t('students.action_error'),
        );
        this.snackbarError(errorMessage);
      } finally {
        this.loader();
      }
    },
    courseDisplayName(course) {
      if (!course || !course.name) return '';
      return course.joinCode ? `${course.name} (${course.joinCode})` : course.name;
    },
    schoolName(schoolId) {
      const school = this.schoolList.find(
        (item) => String(item.value) === String(schoolId),
      );
      return school ? school.text : `${this.$t('students.school')} ${schoolId}`;
    },
    addCourse(courseId) {
      if (!courseId) return;
      const id = String(courseId);
      if (this.userToSave.courses.some((current) => String(current) === id)) return;
      this.userToSave.courses = [...this.userToSave.courses, id];
      const course = this.courses.find((item) => String(item.id) === id);
      if (course && !this.assignedCourses.some((item) => String(item.id) === id)) {
        this.assignedCourses = [...this.assignedCourses, course];
      }
      this.$nextTick(() => {
        this.courseSearch = null;
      });
    },
    // Quitar / deshacer en un solo gesto: el cambio queda pendiente hasta Guardar.
    toggleRemoveCourse(id) {
      const key = String(id);
      const isPresent = this.userToSave.courses.some((current) => String(current) === key);
      if (isPresent) {
        this.userToSave.courses = this.userToSave.courses.filter(
          (current) => String(current) !== key,
        );
      } else {
        this.userToSave.courses = [...this.userToSave.courses, key];
      }
    },
    discardChanges() {
      if (!this.originalSnapshot) return;
      const snap = JSON.parse(this.originalSnapshot);
      this.userToSave.firstname = snap.firstname;
      this.userToSave.lastname = snap.lastname;
      this.userToSave.lastname2nd = snap.lastname2nd;
      this.userToSave.dni = snap.dni;
      this.userToSave.email = snap.email;
      this.userToSave.country = snap.country;
      this.userToSave.active = snap.active;
      this.userToSave.school = snap.school;
      this.userToSave.birthDate = snap.birthDate;
      this.userToSave.courses = [...this.originalCourseIds];
    },
    beforeUnloadHandler(event) {
      if (this.isDirty) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      }
    },
    confirmLeave() {
      this.leaveDialog = false;
      if (this.pendingLeaveNext) this.pendingLeaveNext();
      this.pendingLeaveNext = null;
    },
    cancelLeave() {
      this.leaveDialog = false;
      if (this.pendingLeaveNext) this.pendingLeaveNext(false);
      this.pendingLeaveNext = null;
    },
    capitalize(name) {
      return name
        .split(' ')
        .map(
          (country) => `${country.charAt(0).toUpperCase()}${country.slice(1).toLowerCase()}`,
        )
        .join(' ');
    },
    requiredRule(field) {
      return [(v) => !!v || this.$t(`students.${field}_required`)];
    },
  },
  mixins: [LoadingMixin, SnackbarMixin],
  watch: {
    date(nValue) {
      if (this.user) this.user.birthDate = nValue;
    },
    'userToSave.country': async function (nValue) {
      if (nValue) {
        await this.getSchools(nValue);
      }
    },
    'userToSave.school': async function (nValue) {
      if (nValue) {
        await this.getCourses(nValue);
      }
    },
  },
};
</script>
<style scoped>
.w-100 {
    width: 100%;
}
.course-empty {
    border: 1px dashed #d0d0d0;
    border-radius: 8px;
    text-align: center;
    padding: 12px;
    color: #757575;
}
.chip-other-text {
    font-size: 0.75rem;
    color: #5e35b1;
}
.unsaved-bar {
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 8px;
    padding: 6px 12px;
}
.course-removed-text {
    text-decoration: line-through;
    color: #9e9e9e;
}
.course-undo-btn {
    min-width: auto;
}
</style>
